import { USER_MENU_ITEMS } from "@/constants/navigation"
import { ListItemText, Menu, MenuItem } from "@mui/material"
import PropTypes from "prop-types"
import React from "react"

/**
 * Renders a user menu with profile, invitations, and sign out options.
 *
 * @param {Object} props
 * @param {Element} props.anchorEl - The element that triggers the menu.
 * @param {Function} props.onClose - The function to call when the menu is closed.
 * @param {Object} props.currentUser - The current user object with id, name, role, permissions, feature_flags, and parent.
 * @returns {JSX.Element} The user menu component.
 */
const UserMenu = React.memo(({ anchorEl, onClose, currentUser }) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={onClose}
      aria-labelledby="user-menu-button"
      aria-controls={anchorEl ? "user-menu" : undefined}
      id="user-menu"
      data-testid="user-menu"
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      slotProps={{
        paper: {
          elevation: 2,
          sx: {
            mt: 1,
            minWidth: 150,
            borderRadius: 1,
            overflow: "visible",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        },
      }}
    >
      <MenuItem
        component="a"
        href={`/${currentUser?.role}${USER_MENU_ITEMS.profile.path}`}
        data-testid="user-menu-profile"
      >
        <ListItemText primary={USER_MENU_ITEMS.profile.label} />
      </MenuItem>
      {currentUser?.parent && (
        <MenuItem component="a" href={USER_MENU_ITEMS.invitations.path}>
          <ListItemText primary={USER_MENU_ITEMS.invitations.label} />
        </MenuItem>
      )}
      <MenuItem component="a" href={USER_MENU_ITEMS.signOut.path} data-method="delete" sx={{ color: "error.main" }}>
        <ListItemText primary={USER_MENU_ITEMS.signOut.label} />
      </MenuItem>
    </Menu>
  )
})

UserMenu.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  onClose: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    parent: PropTypes.bool,
    role: PropTypes.string,
  }).isRequired,
}

UserMenu.displayName = "UserMenu"

export default UserMenu
